import { PlayCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { safeUrl } from "@/lib/sanitize";
import { listSystemEvents } from "@/features/knowledge/api";
import type { KbSystemEvent } from "@/features/knowledge/types";

const TABS = [
  { type: "VID_FEATURES", label: "Software Features" },
  { type: "VID_TRANSCRIPTS", label: "E-Services & Transcripts" },
  { type: "VID_TOOLBOX", label: "Marketing Toolbox" },
  { type: "VID_SETUP", label: "Software Setup" },
  { type: "VID_EDUCATION", label: "Continuing Education" },
];

function VideoGrid({ type }: { type: string }) {
  const [items, setItems] = useState<KbSystemEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listSystemEvents(type)
      .then((data) => !cancelled && setItems(data))
      .catch(() => !cancelled && toast.error("Could not load videos."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [type]);

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-48 w-full" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return <p className="text-muted-foreground text-sm">No videos in this section yet.</p>;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <a key={item.id} href={safeUrl(item.href)} target="_blank" rel="noopener noreferrer">
          <Card className="hover:shadow-card-hover h-full gap-0 overflow-hidden p-0 transition-shadow">
            <div className="bg-muted flex aspect-video items-center justify-center">
              {item.detail ? (
                <img
                  src={safeUrl(item.detail)}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <PlayCircle className="text-muted-foreground size-10" />
              )}
            </div>
            <div className="p-4">
              <p className="font-medium">{item.title}</p>
            </div>
          </Card>
        </a>
      ))}
    </div>
  );
}

export default function VideosPage() {
  return (
    <Tabs defaultValue="VID_FEATURES" className="w-full">
      <TabsList className="flex-wrap">
        {TABS.map((t) => (
          <TabsTrigger key={t.type} value={t.type}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {TABS.map((t) => (
        <TabsContent key={t.type} value={t.type} className="mt-4">
          <VideoGrid type={t.type} />
        </TabsContent>
      ))}
    </Tabs>
  );
}
